<template>
  <main class="smarte-gemuetlichkeit">
    <GridContainer padding>
      <SingleLevelBreadcrumb label="Smarte Gemütlichkeit" />

      <Card
        v-bind="card"
        class="smarte-gemuetlichkeit__header"
      />
    </GridContainer>

    <GridContainer class="smarte-gemuetlichkeit__offers">
      <OfferTilesContainer
        v-if="offers && offers?.length > 0"
        :offers="offers"
      />
      <Paragraph
        v-else
        align="center"
      >
        Smarte Gemütlichkeit in der kalten Jahreszeit
      </Paragraph>
    </GridContainer>
  </main>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useLazyAsyncData, useNuxtApp } from '#imports';
import { GridContainer, Paragraph, Card } from '@i22/td-longtail-vue-components';
import type { CardComponentProps } from '@i22/td-longtail-vue-components/dist/components/card/card.vue';
import fetchOffers, { type ListOffer } from '~/lib/goliath/offers';
import { getHeader } from '~/helpers/page-header-compositon';
import { Track } from '~/helpers/page-tracking';

import OfferTilesContainer from '~/components/offer-tiles-container.vue';
import SingleLevelBreadcrumb from '~/components/single-level-breadcrumb.vue';

Track();
getHeader({
  title: ref('Smarte Gemütlichkeit in der kalten Jahreszeit'),
  description: ref(
    'Ob smarte Heizungssteuerung, Tür- und Fenstersensoren oder Heizsets: Jetzt die smarten Produkte für die kalte Jahreszeit entdecken!'
  ),
  isIndexed: ref(false),
});

const card: CardComponentProps = {
  heading: {
    tag: 'h1',
    size: 'lg',
    text: 'Smarte Gemütlichkeit in der kalten Jahreszeit',
  },
  imagePosition: 'right',
  text: 'Mit smarten Heizthermostaten, Heizsets und Tür-Fensterkontakten die perfekten Temperatureinstellungen automatisieren.',
  imageBackground: {
    src: '/content/content-images/promotions/heating-november/6d691a699a1e2c03180d70d506cc1e1c/shop-telekom-startseite-stage-smart-home-einbruchschutz-s-bg@2x.jpg',
    srcSm:
      '/content/content-images/promotions/heating-november/5c47633e7203b619a477e3281f8c8209/shop-telekom-startseite-stage-smart-home-heizen-m-bg@2x.jpg',
    srcMd:
      '/content/content-images/promotions/heating-november/0157e81c0285046a2ae0dd2a4d78a6da/shop-telekom-startseite-stage-smart-home-heizen-bg@2x.jpg',
    sizes: 'xs:120vw sm:100vw md:150vw lg:2560px',
    aspectRatio: '1280 / 1040',
    aspectRatioSm: '2048 / 1040',
    aspectRatioMd: '3840 / 1040',
  },
  image: {
    src: '/content/content-images/promotions/heating-november/4e7a985a329b6b98a74fb5ffce2678ba/smarte-geraete-kollekton-heizen@2x.png',
    sizes: 'xs:600px sm:100vw md:800px',
  },
};

const nuxtApp = useNuxtApp();

const { data: offers } = useLazyAsyncData(
  'smarte-gemuetlichkeit',
  async (): Promise<ListOffer[]> => {
    const data = await fetchOffers({
      filter: {
        articleNumbers: [
          '4029164136017', // Homematic IP Starter Set Heizen Basic für 3 Heizkörper
          '4029164129545', // Homematic IP Heizkörperthermostat – basic 5er Set
          '4250494923484', // Homematic IP Heizkörperthermostat 3er Set
          '4029164137250', // Homematic IP Starter Bundle Fußbodenheizung für 4 Räume
          '4025125534335', // SmartHome Tür-/Fensterkontakt optisch
          '3700730503204', // Netatmo Smarte Tür- und Fenstersensoren
          '4068568997596', // EVE Door & Window (Matter) 2er Pack
          '3700730500258', // Netatmo Smartes Thermostat (DE)
          '3700730501576', // Netatmo Zusätzliches Smartes Heizkörperthermostat (DE
          '3700730501569', // Netatmo Smarte Heizkörperthermostate Starterpaket (DE)
          '3700730503433', // Netatmo 3 Zusätzliche Heizkörperthermostate
        ],
      },
      includeAggregations: false,
    }).then((response) => response?.entries || []);

    if (!data || data.length === 0) {
      nuxtApp.nuxt2Context.redirect('/');
    }

    return data;
  }
);
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.smarte-gemuetlichkeit {
  &__header {
    --card-container-justify: center;
    --card-container-content-flex: 0 0 auto;
    margin-bottom: 1rem;

    @include mq($mq-sm) {
      --card-container-height: 400px;
      --card-image-background-object-position: 100%;
      --card-image-position-left: -100%;
      --image-btlr: var(--card-radius);
      --image-bblr: var(--card-radius);

      :deep(.card__image) {
        z-index: 0;
        max-height: 400px;
      }
    }

    @include mq($mq-md) {
      --card-image-background-object-position: left;
    }

    :deep(.card__container) {
      @include mq($mq-sm) {
        padding-left: 5rem;
      }
    }
  }

  &__offers {
    margin-bottom: $grid-base * 2;
  }
}
</style>
